import * as React from 'react';

import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import { CallHistoryMethodAction, push } from 'connected-react-router';

import { Translate } from 'react-localize-redux';

import { rgba } from 'polished';
import styled, { css } from 'styled-components';

import AppState from '@webapp/store/state';
import { UPDATE_MERCH, UPLOAD_MERCH_IMAGE } from '@webapp/store/merch/actions';
import { ImageWithPositionPlaceHolder, Merch } from '@webapp/store/merch/state';
import {
  EditMerchImage,
  UpdateMerchProductError,
  UploadMerchImage,
} from '@webapp/store/merch/action-creators';

import { AssetFile } from '@common/types/data/asset-file';

import {
  merchReleaseStepCompletedEvent,
  merchReleaseStepViewedEvent,
} from '@webapp/utils/rudderstack/events';

import { MerchProductColor } from '@services/merch/types/api/merch-product-color';

import { MerchProductPositionWithHeight } from '@services/merch/types/api/create-product';
import {
  UploadImageRequest,
  UploadImageResponse,
} from '@services/merch/types/api/upload-image';

import Box from '@components/layout/box';
import Typography from '@components/basics/typography';
import ErrorMessage from '@components/basics/error-message';
import MotionOpacity from '@components/motion/motion-opacity';
import CounterFixedButton from '@components/actions/counter-fixed-button';

import LoadingCtrl from '@webapp/controllers/loading';
import MerchArtSlider from '@webapp/controllers/merch/merch-art-slider';
import UploadMerchArtCtrl from '@webapp/controllers/upload/upload-merch-art';
import Grid from '@components/basics/grid';
import { AnimatePresence } from 'framer-motion';

interface MerchDesignPageProps {
  className?: string;
  active?: Partial<Merch>;
  isEdit?: boolean;
  isMobile?: boolean;
  isTablet?: boolean;
}

interface MerchDesignStateProps {
  artistId?: string;
  artistName?: string;
  images?: Partial<
    UploadImageRequest &
      ImageWithPositionPlaceHolder &
      UploadImageResponse &
      Partial<AssetFile>
  >[];
  nextSection: () => CallHistoryMethodAction;
  uploadImage: (data: UploadImageRequest, artistId: string) => UploadMerchImage;
  editMerchImage: (
    merch: Partial<Merch['image']>,
    id?: string
  ) => EditMerchImage;
  isLoading: boolean;
  sendAnalytics: (
    field: string,
    value: string,
    error: string,
    releaseId?: string
  ) => UpdateMerchProductError;
  selectedColors: string[] | undefined;
  availablePositions?: MerchProductPositionWithHeight[];
  productId?: string;
  printifyProductId?: number;
  previewImages: MerchProductColor[];
  placeholderImage: string;
  sampleImages?: string[];
}

const mapStateToProps = (state: AppState) => {
  const selectedColors = state.merch.active?.selectedColors;

  let placeholderImage = '';
  if (selectedColors?.length) {
    const defaultColor = state.merch.active?.colorSize?.colors.filter(
      (color) => selectedColors.includes(color.label) && color.src
    )[0];

    if (defaultColor?.src) {
      placeholderImage = defaultColor.src;
    } else {
      console.log('placeholderImage_error: ', defaultColor); // eslint-disable-line no-console
    }
  } else if (!!state.merch.active?.product?.images.length) {
    placeholderImage = state.merch.active?.product?.images[0];
  }
  const editProduct = state.merch.active?.editProduct;
  const newProduct = state.merch.active?.newProduct;

  const previewImages = editProduct
    ? editProduct.colors
    : newProduct?.previewColors || [];

  const sampleImages = newProduct?.sampleImages
    ? newProduct.sampleImages
    : editProduct?.sampleImages
    ? editProduct.sampleImages
    : [];

  return {
    artistId: state.artist.active?.id,
    artistName: state.artist.active?.name,
    images: state.merch.active?.image,
    isLoading: !!state.ui.loading[UPLOAD_MERCH_IMAGE || UPDATE_MERCH],
    selectedColors: state.merch.active?.colorSize?.colors?.reduce(
      (acc: string[], color) => {
        if (color.color && !acc.includes(color.color)) {
          return [...acc, color.color];
        }
        return acc;
      },
      []
    ),
    productId:
      state.merch.active?.editProduct?.id ||
      state.merch.active?.newProduct?.productId,
    printifyProductId: state.merch.active?.editProduct
      ? state.merch.active?.editProduct.printify[0].itemId
      : state.merch.active?.product?.id,
    availablePositions: state.merch.active?.availablePositions ?? [],
    previewImages,
    placeholderImage,
    sampleImages,
    isMobile: state.ui.mobile,
    isTablet: state.ui.tablet,
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
  editMerchImage: (imageData: Partial<Merch['image']>, id?: string) =>
    dispatch(new EditMerchImage(imageData, id)),
  nextSection: () => dispatch(push('./pricing-owners')),
  uploadImage: (data: UploadImageRequest, artistId: string) =>
    dispatch(new UploadMerchImage(data, artistId)),
  sendAnalytics: (field: string, value: string, error: string) =>
    dispatch(new UpdateMerchProductError(error, field, value)),
});

export const MerchDesignPage: React.FC<
  MerchDesignPageProps & MerchDesignStateProps
> = ({
  className,
  nextSection,
  uploadImage,
  editMerchImage,
  artistId,
  artistName,
  images,
  previewImages,
  isLoading,
  sendAnalytics,
  selectedColors,
  availablePositions,
  productId,
  printifyProductId,
  placeholderImage,
  sampleImages,
  isEdit,
  isMobile,
  isTablet,
}) => {
  const [artworkErrorId, setArtworkErrorId] = React.useState('');
  const [artworkError, setArtworkError] = React.useState('');

  React.useEffect(() => {
    merchReleaseStepViewedEvent({
      step: 3,
      name: 'select design',
      artist_id: artistId,
      artist_name: artistName,
      product_id: productId,
      product_type: printifyProductId,
      is_edit: isEdit,
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    if (artworkErrorId) {
      sendAnalytics('Merch Art Work', '', artworkErrorId);
    }
  }, [artworkErrorId, sendAnalytics]);

  const handleUploadSuccess = (fileKey: string, fileName: string) => {
    if (artistId) {
      uploadImage({ key: fileKey, fileName: fileName }, artistId);
    }
  };

  const handleNextStepClick = () => {
    if (images && productId) {
      merchReleaseStepCompletedEvent({
        step: 3,
        name: 'select design',
        artist_id: artistId,
        artist_name: artistName,
        product_id: productId,
        product_type: printifyProductId,
        is_edit: isEdit,
      });
      nextSection();
    }
  };

  return (
    <MotionOpacity className={className}>
      {!isMobile && (
        <Typography className="mb-30" customFont="semi-bold" type="subheader">
          <Translate id="MERCH.SECTIONS.DESIGN.TITLE" />
        </Typography>
      )}

      <Grid gap={3}>
        <PreviewBox noBorderBottom loading={isLoading}>
          <AnimatePresence>
            <MotionOpacity>
              <MerchArtSlider
                merchColors={previewImages}
                colors={selectedColors}
                placeholderImage={placeholderImage}
                sampleImages={sampleImages}
              />
            </MotionOpacity>
          </AnimatePresence>

          {isLoading ? (
            <LoadingCtrl className="loader" loading={isLoading} size="128px" />
          ) : null}
        </PreviewBox>

        <Box noBorderTop>
          <UploadMerchArtCtrl
            activeImages={images}
            onChange={(assets, id) => assets && editMerchImage(assets, id)} // changes to be made here now to store images in redux store
            onUpload={handleUploadSuccess}
            onError={setArtworkErrorId}
            onErrorMessage={setArtworkError}
            multipleFiles={availablePositions?.length || 1}
          />
        </Box>
        <ErrorMessage
          hasBackground
          errorId={artworkErrorId ? artworkErrorId : undefined}
          message={artworkError}
        />
      </Grid>

      <CounterFixedButton
        id="productDesign"
        className={!isTablet ? '' : 'mt-30'}
        fixed={!isTablet}
        isValid={!!images?.length}
        currentStep={3}
        maxStep={4}
        isLoading={false}
        onNext={handleNextStepClick}
      />
    </MotionOpacity>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(MerchDesignPage);

const PreviewBox = styled(Box)<{ loading: boolean }>`
  position: relative;
  max-width: 100%;
  overflow: hidden;
  ${({ loading }) =>
    loading
      ? css`
          &:after {
            content: '';
            position: absolute;
            top: 0;
            right: 0;
            bottom: 0;
            left: 0;
            background-color: ${({ theme }) => rgba(theme.colors.primary, 0.5)};
            z-index: 999999;
          }

          .loader {
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            z-index: 999999;
          }
        `
      : null}
`;
